import type { ClientPhoto, PhotoVisibility } from '@salonomy/types';
import { ApiClientError } from '../../lib/api.js';

/** Human labels for the fail-closed visibility ladder (server is authoritative; these only display). */
export const VISIBILITY_LABEL: Record<PhotoVisibility, string> = {
  salon_private: 'Salon only',
  client_visible: 'Client can see',
  showcase: 'Public showcase',
};

/** A short explainer per rung — shown under the selector so staff understand who can see the set. */
export const VISIBILITY_HINT: Record<PhotoVisibility, string> = {
  salon_private: 'Only your salon’s staff can see this set.',
  client_visible: 'The client sees this set in their portal.',
  showcase: 'Public marketing likeness — needs the client’s portfolio consent.',
};

/** Pill tone per rung — neutral private, cobalt shared-with-client, success public showcase. */
export function visibilityTone(v: PhotoVisibility): 'neutral' | 'cobalt' | 'success' {
  return v === 'showcase' ? 'success' : v === 'client_visible' ? 'cobalt' : 'neutral';
}

/**
 * One before/after SET, grouped by pairId. `before`/`after` hold the (single, latest) ready-or-pending
 * photo of each kind; `consentMarketing` is true once portfolio consent is recorded for the set; `visibility`
 * is the set's effective rung (the set moves as a unit — we read the 'before' if present, else the 'after').
 */
export interface PhotoSet {
  pairId: string;
  before: ClientPhoto | null;
  after: ClientPhoto | null;
  /** Newest createdAt across the set's photos — drives the (newest-first) ordering. */
  createdAt: string;
  consentMarketing: boolean;
  visibility: PhotoVisibility;
}

/**
 * Group a flat photo list into before/after SETS by pairId (the LRU-15 unit), newest set first. Within a
 * set we keep the most-recent photo per kind. The set's visibility/consent are taken from a representative
 * photo (the server keeps them consistent across the pair; we prefer the 'before').
 */
export function groupIntoSets(photos: ClientPhoto[]): PhotoSet[] {
  const byPair = new Map<string, ClientPhoto[]>();
  for (const p of photos) {
    const arr = byPair.get(p.pairId);
    if (arr) arr.push(p);
    else byPair.set(p.pairId, [p]);
  }

  const sets: PhotoSet[] = [];
  for (const [pairId, group] of byPair) {
    const newestOf = (kind: 'before' | 'after'): ClientPhoto | null => {
      const ofKind = group
        .filter((p) => p.kind === kind)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      return ofKind[0] ?? null;
    };
    const before = newestOf('before');
    const after = newestOf('after');
    const rep = before ?? after ?? group[0];
    // group always has ≥1 (it only exists because a photo populated it); fall back defensively anyway.
    const createdAt =
      group.map((p) => p.createdAt).sort((a, b) => b.localeCompare(a))[0] ??
      rep?.createdAt ??
      new Date(0).toISOString();
    sets.push({
      pairId,
      before,
      after,
      createdAt,
      consentMarketing: rep?.consentMarketing ?? false,
      visibility: rep?.visibility ?? 'salon_private',
    });
  }

  return sets.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * The HONEST disabled-storage signal. The init route returns 503 ('photo storage not configured') on
 * staging (no bucket creds), and may also surface an `{ enabled: false }` shape. We treat EITHER as
 * "storage isn't configured yet" so the gallery shows the honest message instead of a broken uploader.
 */
export function isStorageDisabledError(err: unknown): boolean {
  if (err instanceof ApiClientError) {
    if (err.status === 503) return true;
    // Tolerate an explicit code if the API uses one rather than a bare 503.
    if (err.code === 'storage_not_configured' || err.code === 'photo_storage_disabled') return true;
  }
  return false;
}
